'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

import {
  Article,
  HeroSimple,
  Heading,
  Paragraph,
  Section,
  Form,
  FormGroup,
  FormInput,
  FormLabel,
  FormInvalidMessage,
  Button,
  ButtonTitle,
  ButtonIcon,
  cn,
} from '@systhemaui/next'

import {
  AuthCard,
  SocialLoginButtons,
  SocialLoginSkeleton,
  PasswordInput,
  PasswordChecklist,
  validatePassword,
  Separator,
  type EnabledProviders,
} from '@/components/auth'

import { signIn, signUp } from '@/lib/auth-client'
import { validateRedirectUrl } from '@/utils/redirectUrl'

const GENERIC_EXISTS_MESSAGE = 'Ez az e-mail cím már regisztrálva van. Lépj be helyette.'

const PROVIDER_LABELS_REG: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
}

const SOCIAL_PROVIDERS_REG = new Set(['google', 'facebook', 'apple'])

async function resolveExistingEmailMessage(email: string): Promise<string> {
  try {
    const res = await fetch('/api/auth/check-provider', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) return GENERIC_EXISTS_MESSAGE
    const data = (await res.json()) as { providers?: string[] }
    const providers = data.providers ?? []
    const socials = providers.filter((p) => SOCIAL_PROVIDERS_REG.has(p))

    if (socials.length === 1) {
      const p = socials[0]
      if (p === 'google') {
        return 'Ez az e-mail cím Google fiókkal van regisztrálva. Lépj be a Google belépéssel.'
      }
      if (p === 'facebook') {
        return 'Ez az e-mail cím Facebook fiókkal van regisztrálva. Lépj be a Facebook belépéssel.'
      }
      if (p === 'apple') {
        return 'Ez az e-mail cím Apple fiókkal van regisztrálva. Lépj be az Apple belépéssel.'
      }
    }

    if (socials.length > 1) {
      const labels = socials.map((p) => PROVIDER_LABELS_REG[p] ?? p).join(', ')
      return `Ez az e-mail cím közösségi fiókkal van regisztrálva (${labels}). Lépj be az egyik közösségi belépéssel.`
    }

    return GENERIC_EXISTS_MESSAGE
  } catch {
    return GENERIC_EXISTS_MESSAGE
  }
}

type RegistrationPageClientProps = {
  redirectParam?: string
  oauthError?: string
}

export default function RegistrationPageClient({
  redirectParam,
  oauthError,
}: RegistrationPageClientProps) {
  const validatedRedirect = validateRedirectUrl(redirectParam ?? null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enabledProviders, setEnabledProviders] = useState<EnabledProviders | null>(null)
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | 'apple' | null>(null)

  const socialError = oauthError
    ? oauthError === 'email_not_found'
      ? 'A regisztrációhoz szükségünk van az e-mail címedre. A közösségi fiókod beállításaiban engedélyezd az e-mail megosztását, majd próbáld újra. Vagy regisztrálj e-mail címmel és jelszóval lent.'
      : oauthError === 'state_mismatch'
        ? 'A regisztrációs folyamat megszakadt (lehet, hogy lejárt vagy duplán kattintottál). Kattints újra a közösségi regisztráció gombra.'
        : 'Nem sikerült a közösségi regisztráció. Próbáld újra, vagy regisztrálj e-mail címmel és jelszóval lent.'
    : null

  useEffect(() => {
    fetch('/api/auth/providers')
      .then((res) => res.json())
      .then((data: EnabledProviders) => setEnabledProviders(data))
      .catch(() => setEnabledProviders({ google: false, facebook: false, apple: false }))
  }, [])

  useEffect(() => {
    const reset = () => setSocialLoading(null)
    window.addEventListener('pageshow', reset)
    window.addEventListener('focus', reset)
    return () => {
      window.removeEventListener('pageshow', reset)
      window.removeEventListener('focus', reset)
    }
  }, [])

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {}

    if (!email) {
      newErrors.email = 'Kötelező mező'
    } else if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(email)) {
      newErrors.email = 'Érvénytelen e-mail cím'
    }

    if (!password) {
      newErrors.password = 'Kötelező mező'
    } else if (!validatePassword(password)) {
      newErrors.password = 'A jelszó nem felel meg a követelményeknek'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setErrors({})

    try {
      const placeholderName = email.split('@')[0] || 'User'
      const result = await signUp.email({
        email,
        password,
        name: placeholderName,
      })
      if (result.error) {
        if (
          result.error.code === 'USER_ALREADY_EXISTS' ||
          result.error.message?.includes('already exists')
        ) {
          const message = await resolveExistingEmailMessage(email)
          setErrors({ email: message })
        } else if (result.error.code === 'PASSWORD_TOO_SHORT') {
          setErrors({ password: 'A jelszónak legalább 8 karakter hosszúnak kell lennie' })
        } else {
          setErrors({
            password:
              'Nem sikerült a regisztráció. Ellenőrizd az adatokat, és próbáld újra. Ha tartós a hiba, írj nekünk.',
          })
        }
        setIsSubmitting(false)
      } else {
        const redirectUrl = validatedRedirect
          ? `/kezdo-lepesek?redirect=${encodeURIComponent(validatedRedirect)}&method=email`
          : '/kezdo-lepesek?method=email'
        window.location.href = redirectUrl
      }
    } catch {
      setErrors({ password: 'Hiba történt a regisztrációnál' })
      setIsSubmitting(false)
    }
  }

  const handleSocialRegister = async (provider: 'google' | 'facebook' | 'apple') => {
    if (socialLoading || isSubmitting) return
    setSocialLoading(provider)
    setErrors({})

    const baseUrl = validatedRedirect || '/'
    const callbackUrl = baseUrl.includes('?')
      ? `${baseUrl}&registered=true`
      : `${baseUrl}?registered=true`

    try {
      const result = await signIn.social({
        provider,
        callbackURL: callbackUrl,
        errorCallbackURL: '/regisztracio',
      })
      if (result?.error) {
        const status = result.error.status
        const message =
          status === 429
            ? 'Túl sok próbálkozás rövid idő alatt. Várj egy fél percet, és próbáld újra.'
            : 'Nem sikerült elindítani a közösségi regisztrációt. Próbáld újra, vagy regisztrálj e-mail címmel és jelszóval lent.'
        setErrors({ email: message })
        setSocialLoading(null)
      }
    } catch {
      setErrors({
        email:
          'Nem sikerült elindítani a közösségi regisztrációt. Próbáld újra, vagy regisztrálj e-mail címmel és jelszóval lent.',
      })
      setSocialLoading(null)
    }
  }

  const hasSocialProviders =
    enabledProviders &&
    (enabledProviders.google || enabledProviders.facebook || enabledProviders.apple)

  return (
    <main id="registration">
      <Article theme="default">
        <HeroSimple alignContent="center">
          <Heading.h1>Regisztráció</Heading.h1>
          <Paragraph type="lead" className="text-balance">
            Hozd létre a CreatorTV profilodat könnyedén és teljesen ingyen!
          </Paragraph>
        </HeroSimple>

        <Section
          theme="default"
          layoutBackground="base"
          padding={3}
          className="pt-0 [&_.col-mx-3]:lg:col-span-12 [&_.col-mx-3]:lg:col-start-7"
        >
          <AuthCard>
            {socialError && (
              <div className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {socialError}
              </div>
            )}

            {!enabledProviders ? (
              <>
                <SocialLoginSkeleton />
                <Separator />
              </>
            ) : hasSocialProviders ? (
              <>
                <SocialLoginButtons
                  mode="register"
                  enabledProviders={enabledProviders}
                  loadingProvider={socialLoading}
                  disabled={isSubmitting}
                  onFacebookClick={() => handleSocialRegister('facebook')}
                  onGoogleClick={() => handleSocialRegister('google')}
                  onAppleClick={() => handleSocialRegister('apple')}
                />
                <Separator />
              </>
            ) : null}

            <Form disableAnimation onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="email">E-mail cím</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                  placeholder="pl.: gipsz.jakab@creatortv.hu"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  className={cn(errors.email && 'is-invalid')}
                  required
                  disabled={socialLoading !== null}
                />
                {errors.email && <FormInvalidMessage>{errors.email}</FormInvalidMessage>}
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="password">Jelszó</FormLabel>
                <PasswordInput
                  id="password"
                  name="password"
                  value={password}
                  onChange={setPassword}
                  isInvalid={!!errors.password}
                  required
                  disabled={socialLoading !== null}
                />
                {errors.password && <FormInvalidMessage>{errors.password}</FormInvalidMessage>}
              </FormGroup>

              <PasswordChecklist password={password} />

              <Button
                type="submit"
                disabled={isSubmitting || socialLoading !== null}
                className="mt-4"
              >
                <ButtonTitle>{isSubmitting ? 'Betöltés...' : 'Regisztrálok'}</ButtonTitle>
                <ButtonIcon
                  dangerouslySetInnerHTML={{
                    __html:
                      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>',
                  }}
                />
              </Button>
            </Form>

            <Separator />

            <Paragraph className="mt-0 mb-4 w-full text-center">
              Már van fiókod?{' '}
              <Link
                href={
                  validatedRedirect
                    ? `/belepes?redirect=${encodeURIComponent(validatedRedirect)}`
                    : '/belepes'
                }
                className="text-[#FE4C04] hover:underline"
              >
                Belépés
              </Link>
            </Paragraph>
          </AuthCard>
        </Section>
      </Article>
    </main>
  )
}
